import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap, catchError, map, delay } from 'rxjs';
import { environment } from '@environments/environment';

export interface ServedAd {
  adId: string;
  impressionId: string;
  adFormat: string;
  adSize?: string;
  headline?: string;
  description?: string;
  imageUrl?: string;
  logoUrl?: string;
  destinationUrl: string;
  displayUrl?: string;
  callToAction?: string;
  sponsoredLabel: string;
  latitude?: number;
  longitude?: number;
  token?: string;
}

export interface AdServingResponse {
  ads: ServedAd[];
}

interface CacheEntry {
  data: ServedAd | null;
  expiry: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const SESSION_KEY = 'gc_ad_session';

// ─── Mock ad data per placement ─────────────────────────────────────────────
const MOCK_ADS: Record<string, ServedAd> = {
  MAP_VIEW: {
    adId: 'mock-map-001', impressionId: 'mock-imp-map-001',
    adFormat: 'BANNER', headline: 'ChargePoint — Find Fast Chargers Near You',
    description: 'Over 30,000 charging spots nationwide. Plan your route with confidence.',
    destinationUrl: 'https://www.chargepoint.com', displayUrl: 'chargepoint.com',
    callToAction: 'LEARN_MORE', sponsoredLabel: 'Sponsored',
  },
  STATION_DETAILS_IMAGE: {
    adId: 'mock-sd-img-001', impressionId: 'mock-imp-sd-img-001',
    adFormat: 'BANNER', headline: 'EVgo — Charge in 20 Minutes',
    description: 'DC fast charging on the go',
    destinationUrl: 'https://www.evgo.com', displayUrl: 'evgo.com',
    callToAction: 'GET_DIRECTIONS', sponsoredLabel: 'Sponsored',
  },
  STATION_DETAILS_ACTIONS: {
    adId: 'mock-sd-act-001', impressionId: 'mock-imp-sd-act-001',
    adFormat: 'NATIVE', headline: 'Electrify America — Ultra-Fast Charging',
    description: 'Charge up to 350kW at over 800 stations. Download the app for exclusive member pricing and real-time availability.',
    destinationUrl: 'https://www.electrifyamerica.com', displayUrl: 'electrifyamerica.com',
    callToAction: 'LEARN_MORE', sponsoredLabel: 'Sponsored',
  },
  STATION_DETAILS_BOTTOM: {
    adId: 'mock-sd-bot-001', impressionId: 'mock-imp-sd-bot-001',
    adFormat: 'BANNER', adSize: '300x250',
    headline: 'Rivian Adventure Network',
    description: 'Exclusive charging for Rivian owners',
    destinationUrl: 'https://rivian.com/experience/charging', displayUrl: 'rivian.com',
    callToAction: 'LEARN_MORE', sponsoredLabel: 'Sponsored',
  },
  SEARCH_RESULTS: {
    adId: 'mock-search-001', impressionId: 'mock-imp-search-001',
    adFormat: 'SPONSORED_LISTING',
    headline: 'ChargePoint Station — Downtown',
    description: '4 DC Fast chargers available. Open 24/7. 0.3 mi away.',
    destinationUrl: 'https://www.chargepoint.com/stations/downtown', displayUrl: 'chargepoint.com',
    callToAction: 'GET_DIRECTIONS', sponsoredLabel: 'Sponsored',
  },
  NAVIGATION_ROUTE: {
    adId: 'mock-nav-rt-001', impressionId: 'mock-imp-nav-rt-001',
    adFormat: 'NATIVE',
    headline: 'Skip the Wait — ChargePoint DC Fast',
    description: '80% charge in 20 min. 2 stations on your route with available ports right now.',
    destinationUrl: 'https://www.chargepoint.com/fast', displayUrl: 'chargepoint.com/fast',
    callToAction: 'LEARN_MORE', sponsoredLabel: 'Sponsored',
  },
  NAVIGATION_BOTTOM: {
    adId: 'mock-nav-bot-001', impressionId: 'mock-imp-nav-bot-001',
    adFormat: 'BANNER',
    headline: 'Tesla Supercharger Network',
    description: 'Now open to all EVs',
    destinationUrl: 'https://www.tesla.com/supercharger', displayUrl: 'tesla.com',
    callToAction: 'LEARN_MORE', sponsoredLabel: 'Sponsored',
  },
};

@Injectable({
  providedIn: 'root',
})
export class AdService {
  private baseUrl = (environment as any).ads?.apiUrl || '';
  private useMock = !!(environment as any).ads?.useMockAds;
  private cache = new Map<string, CacheEntry>();
  private sessionId: string;

  private static uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  constructor(private http: HttpClient) {
    // Session ID for frequency capping
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = 'sess_' + AdService.uuid();
      localStorage.setItem(SESSION_KEY, sid);
    }
    this.sessionId = sid;

    if (this.useMock) {
      console.log('%c[MockAds] Mock ads enabled — set environment.ads.useMockAds = false to use real API', 'color: #FF6F00; font-weight: bold');
    }
  }

  isEnabled(): boolean {
    return !!(environment as any).ads?.enabled;
  }

  isPlacementEnabled(placement: string): boolean {
    if (!this.isEnabled()) return false;
    const placements = (environment as any).ads?.placements;
    if (!placements) return false;
    return !!placements[placement];
  }

  /**
   * Get an ad for a placement. Returns null if no ad available, placement disabled, or API error.
   * Uses 5-minute TTL cache per placement to avoid excessive API calls.
   */
  getAd(placement: string, geoContext?: { country?: string; state?: string; city?: string }): Observable<ServedAd | null> {
    const configKey = this.placementToConfigKey(placement);
    if (!this.isPlacementEnabled(configKey)) {
      return of(null);
    }

    // Mock mode — return fake ad data
    if (this.useMock) {
      const mockAd = MOCK_ADS[placement] || null;
      return of(mockAd).pipe(delay(200));
    }

    // Check cache
    const cached = this.cache.get(placement);
    if (cached && cached.expiry > Date.now()) {
      return of(cached.data);
    }

    const deviceType = this.getDeviceType();

    return this.http.post<AdServingResponse>(`${this.baseUrl}/serve/ads`, {
      placement,
      geoCountry: geoContext?.country || 'US',
      geoState: geoContext?.state,
      geoCity: geoContext?.city,
      deviceType,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    }).pipe(
      map(res => res.ads?.[0] || null),
      tap(ad => {
        this.cache.set(placement, { data: ad, expiry: Date.now() + CACHE_TTL });
      }),
      catchError(() => {
        // Graceful degradation — silently hide on error
        this.cache.set(placement, { data: null, expiry: Date.now() + CACHE_TTL });
        return of(null);
      }),
    );
  }

  /**
   * Get MAP_PIN ads for the current map viewport. Returns all matching sponsored pins.
   */
  private mapPinCache: { data: ServedAd[]; expiry: number } | null = null;

  getMapPins(bounds: { neLat: number; neLng: number; swLat: number; swLng: number }): Observable<ServedAd[]> {
    if (!this.isPlacementEnabled('mapPin')) return of([]);
    if (this.useMock) return of([]).pipe(delay(200));

    // 5-minute cache — return cached pins regardless of viewport change
    if (this.mapPinCache && this.mapPinCache.expiry > Date.now()) {
      return of(this.mapPinCache.data);
    }

    return this.http.post<AdServingResponse>(`${this.baseUrl}/serve/ads`, {
      placement: 'MAP_PIN',
      deviceType: this.getDeviceType(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      viewportNeLat: bounds.neLat,
      viewportNeLng: bounds.neLng,
      viewportSwLat: bounds.swLat,
      viewportSwLng: bounds.swLng,
    }).pipe(
      map(res => res.ads || []),
      tap(pins => {
        this.mapPinCache = { data: pins, expiry: Date.now() + CACHE_TTL };
      }),
      catchError(() => of([])),
    );
  }

  /**
   * Record a MAP_PIN impression when the user taps the pin (popup opens).
   * Returns the impressionId for subsequent click tracking.
   */
  recordPinImpression(adId: string, token?: string): Observable<string | null> {
    return this.http.post<{ impressionId: string }>(`${this.baseUrl}/serve/pin-impression`, {
      adId,
      token,
      deviceType: this.getDeviceType(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    }).pipe(
      map(res => res.impressionId),
      catchError(() => of(null)),
    );
  }

  /**
   * Record an ad click and return the destination URL.
   */
  recordClick(ad: ServedAd, geoContext?: { country?: string; state?: string; city?: string }): void {
    if (!this.useMock) {
      this.http.post(`${this.baseUrl}/serve/clicks`, {
        adId: ad.adId,
        impressionId: ad.impressionId,
        geoCountry: geoContext?.country || 'US',
        geoState: geoContext?.state,
        geoCity: geoContext?.city,
        deviceType: this.getDeviceType(),
        token: ad.token,
      }).subscribe(); // Fire and forget
    } else {
      console.log('%c[MockAds] Click recorded:', 'color: #FF6F00', ad.adId, ad.destinationUrl);
    }

    window.open(ad.destinationUrl, '_blank');
  }

  private getDeviceType(): string {
    return window.innerWidth <= 768 ? 'MOBILE' : 'DESKTOP';
  }

  private placementToConfigKey(placement: string): string {
    const map: Record<string, string> = {
      'MAP_VIEW': 'mapView',
      'MAP_PIN': 'mapPin',
      'STATION_DETAILS_IMAGE': 'stationDetailsImage',
      'STATION_DETAILS_ACTIONS': 'stationDetailsActions',
      'STATION_DETAILS_BOTTOM': 'stationDetailsBottom',
      'SEARCH_RESULTS': 'searchResults',
      'NAVIGATION_ROUTE': 'navigationRoute',
      'NAVIGATION_BOTTOM': 'navigationBottom',
    };
    return map[placement] || placement;
  }
}
